import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Data, NavigationStart, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService, AuthService } from 'app/core';
import { NewPermission, Permission, ReservedRole } from 'app/core/enums/permission';
import {
  Account,
  FieldDef,
  InvestmentType,
  ProjectPhase,
  ProjectStage,
  ProjectType,
  PropertyType,
  Vehicle,
} from 'app/core/models/account.types';
import { User } from 'app/core/models/user.types';
import { UserService } from 'app/layout/common/user/user.service';
import { LayoutService } from 'app/layout/layout.service';
import { ProjectsListComponent } from 'app/modules/projects/list/list.component';
import { ProjectAddComponent } from 'app/modules/projects/modals/project-add/project-add.component';
import { ProjectChangeLayoutsComponent } from 'app/modules/projects/modals/project-change-layouts/project-change-layouts.component';
import { ProjectChangeStatusComponent } from 'app/modules/projects/modals/project-change-status/project-change-status.component';
import { ProjectEditPipelineComponent } from 'app/modules/projects/modals/project-edit-pipeline/project-edit-pipeline.component';
import { ProjectsFilterSaveComponent } from 'app/modules/projects/modals/projects-filter-save/projects-filter-save.component';
import { ProjectsFilterComponent } from 'app/modules/projects/modals/projects-filter/projects-filter.component';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline, Project, ProjectPagination } from 'app/modules/projects/projects.types';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import {
  generateDownloadableFile,
  getAllowedFields,
  getHeaderLabel,
  getLinkedFieldValue as _getLinkedFieldValue,
  isLinkedFieldColumn,
  removeArchivedFieldsFromTableDefinition,
} from 'app/shared/functions';
import { Dictionary } from 'app/shared/models/dictionary';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { isString } from 'app/shared/Utils/common.utils';
import UrlUtils from 'app/shared/Utils/url.utils';
import clone from 'lodash-es/clone';
import each from 'lodash-es/each';
import first from 'lodash-es/first';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import _groupBy from 'lodash-es/groupBy';
import keys from 'lodash-es/keys';
import map from 'lodash-es/map';
import max from 'lodash-es/max';
import sortBy from 'lodash-es/sortBy';
import { plural } from 'pluralize';
import { EMPTY, merge, noop, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, switchMap, take, tap } from 'rxjs/operators';

import { FilterListItemValueModel, RedirectItem, TsSnackbarService, TsTableService } from 'tsui';
import { setupAddDealByDrawer } from '../../@shared/functions.utils';
import { MergeProjectService } from '../../@shared/services/merge-project.service';
import { PipelineAddComponent } from '../../modals/pipeline-add/pipeline-add.component';
import { ProjectStatusRequiredFieldsComponent } from '../../modals/project-status-required-fields/project-status-required-fields.component';
import { PipelineService } from '../../pipeline.service';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { UpdateProjectStatusService } from '../../@shared/services/update-project-status.service';
import { MentionConfig } from 'angular-mentions';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { Store } from '@ngxs/store';
import { FieldDefsState } from '@state/fields/state';
import {
  CellEditingStoppedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IsGroupOpenByDefaultParams,
  SortChangedEvent,
  SortState,
} from 'ag-grid-enterprise';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { UserState } from '@state/user/state';
import { PERMISSION_DENY_LABEL } from '@shared/literals';
import { DealsGroupTableRenderer } from '../../../../shared/components/ts-table-renderers/deals-group-renderer/table-deals-group-renderer.component';
import { TsTableTagsEditor } from '@shared/components/ts-table-renderers/editors/table-tags-editor.component';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { TsTableGroupedService } from 'tsui/lib/services/ts-table-grouped.service';
import { ChangeType, OriginalStates, TableSortState } from '@shared/models';
import { QueryConfig } from '@core/models';
import { PortfolioMergeComponent } from '../../modals/portfolio-merge/portfolio-merge.component';
import { nonEditableStyle } from 'tsui/lib/cross-cutting/util';
import { PortfolioActionComponent } from '../../modals/portfolio-action/portfolio-action.component';
import { Dialog } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { MentionsService } from '@shared/services/mentions.service';
import { TableNextTaskRendererComponent } from '@shared/components/ts-table-renderers/table-next-task-renderer/table-next-task-renderer.component';

export class Group {
  fieldName: string;
  fieldVal: any;
  label: string;
  totalCount = 0;
  summaryColumns: any = {};
}

type RouteData = Data & { fieldDefs: FieldDef[]; pipeline: Pipeline; options: { [key: string]: any } };

type ProjectGroup = {
  totalCount: number;
  fieldName: string;
  label: string;
  summaryColumns: { [key: string]: { total: number; average: number } };
};

type ProjectGroupOrProject = ProjectGroup | Project;

@Component({
  selector: 'projects-list-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ProjectsListTableComponent
  extends OnDestroyMixin
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  account: Account;
  pipeline: Pipeline;
  pipelines: Pipeline[];
  projects$: Observable<Project[]>;
  projects: Project[];
  pagination: ProjectPagination;
  projectsCount: number;
  isLoading: boolean;
  exportingCsv: boolean;
  exportingPdf: boolean;
  searchInputControl: UntypedFormControl;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  tableDisplayColumns: string[] = [];
  selection = new SelectionModel<number>(true, []);
  groupedSummaryColumns: string[] = [];
  displayColumnsEditMode: boolean;
  data: RouteData;
  displayGroupedType = 'total';
  uploadingFile = false;
  refreshMode: 'All' | 'Group' = 'All';
  PERMISSION_DENY_LABEL = PERMISSION_DENY_LABEL;

  public groupedProjects: Array<ProjectGroupOrProject> = [];
  // Used to apply chunked array when 100 items per page is selected (performance tuneup).
  public chunkedProjectsArray: Array<ProjectGroupOrProject> = [];
  defaultGroupBy = 'project_stage_id';
  sortBy: any = { field: 'project_stage_rank', order: 'asc' };
  private _sortField: string = 'project_stage_rank';
  private _sortDirection: 'asc' | 'desc' = 'asc';
  private sortChangedSubject = new Subject<void>();
  private _changeTriggers: Set<string> = new Set();
  private _originalStates: OriginalStates = {};

  fieldDefs: FieldDef[] = [];
  defaultFieldDefs: FieldDef[] = [];
  filteredFieldDefs: FieldDef[] = [];
  queryFilters: any;
  savedFilter: any = null;
  Permission = Permission;
  mentionConfig: MentionConfig = this.mentionsService.getFieldMentionConfig();

  // Filtering
  @ViewChild('expandFiltersButton', { static: true }) expandFiltersButton: ElementRef;
  @ViewChild('filters', { static: true }) filters: ElementRef;
  isFilterOpen = false;
  filterRows = [];
  newFilterFields = [
    { name: 'City', type: 'string' },
    { name: 'State', type: 'string' },
    { name: 'Zip', type: 'string' },
    { name: 'Market', type: 'string' },
    { name: 'Created After', type: 'date' },
    { name: 'Created Before', type: 'date' },
    { name: 'Status', type: 'status' },
    { name: 'Property Type', type: 'property_type' },
    { name: 'Deal Type', type: 'deal_type' },
    { name: 'Investment Type', type: 'investment_type' },
    { name: 'Vehicle', type: 'vehicle' },
    { name: 'Assigned To', type: 'assigned_to' },
  ];
  canAddUserToPipeline = false;
  stringFilters = ['Equals'];
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  projectPhases: ProjectPhase[];
  vehicles: Vehicle[];
  investmentTypes: InvestmentType[];
  users: User[];
  filterForm: UntypedFormGroup;
  userRole: any;
  optionsFilterFn = {};
  public isUserAdmin: boolean;
  public isLoadingChildren: Array<boolean> = [];
  protected readonly NewPermission = NewPermission;

  // Private
  private _fieldDefsPanelOverlayRef: OverlayRef;
  private _fieldsDefsPanelTemplatePortal: TemplatePortal;
  private _customFieldPanelTemplatePortal: TemplatePortal;
  private _customFieldPanelOverlayRef: OverlayRef;
  private _defaultPageSize = 25;
  private _pageSize = 25;
  private _pageSizeOptions = [5, 10, 25, 50, 100];
  private isDataUpdating = false;
  private previousSelectedIds: number[] = [];

  @ViewChild('fieldDefsPanel', { static: true }) private _fieldDefsPanel: TemplateRef<any>;
  @ViewChild('fieldDefsPanelOrigin', { static: true }) private _fieldDefsPanelOrigin: MatButton;
  @ViewChild('customFieldPanel', { static: true }) private _customFieldPanel: TemplateRef<any>;
  @ViewChild('customFieldPanelOrigin', { static: true }) private _customFieldPanelOrigin: MatButton;
  @ViewChild(MatSort, { static: false }) private _sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) private _paginator: MatPaginator;

  @ViewChild('tableWrapper') private _tableWrapper: ElementRef<HTMLDivElement>;

  private get tableWrapperElement(): HTMLDivElement {
    return this._tableWrapper.nativeElement;
  }

  private tableScrollPosition: { left: number; top: number } = { left: 0, top: 0 };
  private resetScrollPosition: boolean = true;

  expandedElement: any | null;
  expandedProject: any | null;
  expandedProjects: number[] = [];
  childrenColumns: any;
  childIds: any[];
  readonly hasNestedFiltersOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));

  pipeableTypes = ['number', 'percent', 'decimal', 'money', 'date', 'datetime', 'year', 'text', 'select', 'textarea'];
  editableDictionary: { [key: string]: boolean } = {};
  editableTooltipDictionary: { [key: string]: string } = {};
  groupByMemento = null;
  fieldSearchValue = '';
  getHeaderLabel = getHeaderLabel;
  isLinkedFieldColumn = isLinkedFieldColumn;
  private lastPipelineIdSelected: number = null;
  private filterApplied = false;
  private groupByLocalSelectionKey = 'viewGroupBy';
  public isLoading$: Observable<boolean>;
  columnDefs: ColumnDef[] = [];
  rowData: any[] = [];
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this._projectsService.getProjectByIdBase(String(key)).pipe(
          untilComponentDestroyed(this),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    } as RedirectItem,
  };
  newTable: boolean = false;
  groupingColumns: ColDef[] = [];

  private gridApi!: GridApi<any>;

  projectsMap = new Map<string, any>();

  autoGroupColumnDef;

  gridOptions = {
    animateRows: true,
    suppressAggAtRootLevel: true,
    enableGroupEdit: true,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 200,
    },
    getRowClass: (params) => {
      if (params.data && params.data?.isCategory === true) {
        return 'category-row';
      }
      return '';
    },
    isGroupOpenByDefault: (params: IsGroupOpenByDefaultParams) => {
      return !!params.rowNode.data?.isCategory || this.expanded(params.rowNode.data);
    },
    loading: true,
    isRowSelectable: (rowNode) => {
      return !rowNode?.data?.isCategory;
    },
  } as Partial<GridOptions>;

  previousSelection: any[] = [];
  private columnResizeSubject = new Subject<void>();

  titleField: FieldDef;
  private dialog = inject(Dialog);

  get groupByLocalSelection(): Dictionary<string> {
    return (UrlUtils.getFromLocalStorage(this.groupByLocalSelectionKey, true) as Dictionary<string>) || {};
  }

  get options(): Dictionary<any> {
    return this.data.options;
  }

  private get _projectTemplates(): { id: number; name: string }[] {
    return this.options.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type == 'Project')
      .sort((a, b) => a.rank - b.rank);
  }

  private get selectedProjects(): Project[] {
    return this.projects.filter(({ id }: Project) => this.selection.selected.includes(Number(id)));
  }

  get enableMerge(): boolean {
    return this.selectedProjects.length > 1 || this.selectedProjects.some(({ parent_id }) => parent_id);
  }

  get enableSplit(): boolean {
    return (
      this.selectedProjects.length > 0 &&
      (this.selectedProjects.some(({ parent_id }) => parent_id) ||
        this.selectedProjects.some((project) => project.is_parent === 'true') ||
        this.selectedProjects.some((project) => project.is_child === 'true'))
    );
  }

  get includeChildrenToggled(): boolean {
    return (Object.keys(this.queryFilters).includes('includeChildren') && this.queryFilters.includeChildren) ?? false;
  }

  constructor(
    public projectsListComponent: ProjectsListComponent,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectsService: ProjectsService,
    private _accountService: AccountService,
    private _userService: UserService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _overlay: Overlay,
    private _formBuilder: UntypedFormBuilder,
    private _viewContainerRef: ViewContainerRef,
    private readonly _snackBar: TsSnackbarService,
    private readonly _layoutService: LayoutService,
    private readonly _pipelineService: PipelineService,
    private readonly _foldersTabService: FoldersTabService,
    private readonly _authService: AuthService,
    private readonly confirmService: ConfirmService,
    private readonly updateProjectStatus: UpdateProjectStatusService,
    private readonly store: Store,
    private readonly tsTableGroupedService: TsTableGroupedService,
    private readonly mentionsService: MentionsService,
  ) {
    super();
    const { hostname, port } = window.location;
    this.searchInputControl = new UntypedFormControl();

    this.pagination = {
      length: 1,
      size: 10,
      page: 1,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };

    this.displayColumns = this.fieldDefs.map((fieldDef) => fieldDef.name);

    this._accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.account = accountData;
    });

    this._userService.role$.pipe(untilComponentDestroyed(this)).subscribe((role) => {
      if (role) {
        this.userRole = role;
        this.isUserAdmin = role.name.includes('admin');
      }
      this._changeDetectorRef.markForCheck();
    });

    this.relatedToRedirectConfig = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);

    this.isLoading$ = this._projectsService.loading$;

    this.optionsFilterFn = {
      project_stage_id: this.stagesFilterFn.bind(this),
    };
  }

  private getNestedOptions(): any {
    return this.hasNestedFiltersOn ? { fieldDefs: this.fieldDefs } : null;
  }

  ngOnInit(): void {
    this.checkTable();
    this.canAddUserToPipeline = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))
      ? this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_SHARING_RECORDS))
      : this.store.selectSnapshot(UserState.hasPermission(Permission.PROJECT_PIPELINE_ADD_USER));

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: RouteData) => {
      this.data = data;
      this.pipeline = data.pipeline;
      UrlUtils.saveToLocalStorage('projectPipeline', this.pipeline.id);
      if (this.hasNestedFiltersOn) {
        this.fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            allowedFieldsOnly: true,
            addNestedFields: true,
          }),
        );
      } else {
        this.fieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.Project);
      }
      this.filteredFieldDefs = this.fieldDefs;
      this.mentionConfig.mentions[1].items = data.fieldDefs;

      sessionStorage.setItem('currentPage', '0');
    });

    this._accountService.fieldDefs$.pipe(untilComponentDestroyed(this)).subscribe((fields) => {
      if (this.hasNestedFiltersOn) {
        this.fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            allowedFieldsOnly: true,
            addNestedFields: true,
          }),
        );
      } else {
        this.fieldDefs = getAllowedFields(fields, RelatedToType.Project);
      }
      this.filteredFieldDefs = this.fieldDefs;
      this.titleField = this.lookupField('title');
    });

    this._accountService.defaultFieldDefs$
      .pipe(untilComponentDestroyed(this))
      .subscribe((fields) => (this.defaultFieldDefs = getAllowedFields(fields, RelatedToType.Project)));

    this._projectsService
      .getFunctions()
      .pipe(untilComponentDestroyed(this))
      .subscribe((functions) => {
        this.mentionConfig.mentions[0].items = functions;
      });

    // Monitor for queryParams changes
    this._activatedRoute.queryParams.pipe(untilComponentDestroyed(this)).subscribe((params) => {
      const currentPage = +sessionStorage.getItem('currentPage') || 0;
      const tableDef =
        typeof this.data.pipeline.table_definition === 'string'
          ? JSON.parse(this.data.pipeline.table_definition)
          : this.data.pipeline.table_definition;
      this.filterApplied = false;
      if (params['filterId']) {
        // If filterId queryParam is equal to reset or clear, reset query filters and update the deals.
        if (params['filterId'] === 'reset' || params['filterId'] === 'clear') {
          this.filterApplied = false;
          this.resetQueryFilters();
          this._projectsService
            .getProjects(
              tableDef.fields.concat(this.data.pipeline.table_definition.groupBy),
              this.queryFilters,
              currentPage,
              this._pageSize,
              this.getSortByArray(),
              this.sortBy.order,
              '',
              null,
              '',
              false,
              true,
              false,
              this.getNestedOptions(),
            )
            .pipe(untilComponentDestroyed(this))
            .subscribe(noop);
        }
        // Else filterId has an ID value, then fetch the related saved filter and update the deals.
        else {
          this.filterApplied = true;
          this._accountService
            .getSavedFilterById(parseInt(params['filterId']))
            .pipe(
              untilComponentDestroyed(this),
              tap((savedFilter) => {
                this.savedFilter = savedFilter;
                this.queryFilters = JSON.parse(savedFilter.filters);
                this.saveQueryFilters(this.queryFilters);
                UrlUtils.saveToLocalStorage('savedFilter', this.savedFilter.id.toString(), false);
              }),
              switchMap(() =>
                this._projectsService.getProjects(
                  tableDef.fields.concat(this.data.pipeline.table_definition.groupBy),
                  this.queryFilters,
                  currentPage,
                  this._pageSize,
                  this.getSortByArray(),
                  this.sortBy.order,
                  this.queryFilters['q'] ?? null,
                  null,
                  '',
                  this.queryFilters?.includeChildren ?? false,
                  true,
                  this.queryFilters?.includeChildren ?? false,
                  this.getNestedOptions(),
                ),
              ),
            )
            .subscribe(noop);
        }
      } else {
        this.fillQueryFilters();
        let id = UrlUtils.getFromLocalStorage('savedFilter', false);
        if (id) {
          let url = this._router.url.split('?')[0];
          if (this.pipeline?.pipeline_type === 'static')
            this._router.navigate([url], { queryParams: { filterId: id } });
          else this._router.navigate([url]);
        }

        this.fillPageSize();
        this.fillSortBy();
      }
    });

    this._projectsService.pipeline$
      .pipe(
        untilComponentDestroyed(this),
        switchMap((pipeline) => {
          const isNotGroupable = (): boolean =>
            this.fieldDefs.find(
              (x) => x.related_to_type === RelatedToType.Project && x.name === pipeline.table_definition.groupBy,
            )?.meta?.groupable !== true;

          if (!pipeline.table_definition.groupBy || isNotGroupable()) {
            return this.updatePipelineGroupBy(pipeline, this.defaultGroupBy);
          }

          if (this.currentGroupByCollumnIsArchivedField(pipeline.table_definition.groupBy, this.fieldDefs)) {
            return this.updatePipelineGroupBy(pipeline, this.defaultGroupBy);
          }

          const newGroupByKey = this.newGroupByKey(pipeline.table_definition.groupBy);
          if (newGroupByKey) {
            return this.updatePipelineGroupBy(pipeline, newGroupByKey);
          }
          return of(pipeline);
        }),
      )
      .subscribe((pipeline: Pipeline) => {
        this.isLoading = true;
        this.pipeline = pipeline;
        this.groupByMemento = pipeline.table_definition.groupBy;
        this.pipeline.table_definition.groupBy =
          this.groupByLocalSelection[pipeline.id] ?? this.pipeline.table_definition.groupBy;
        if (!this.pipeline.table_definition.fields) {
          this.pipeline.table_definition.fields = this.displayColumns;
        }
        this._changeDetectorRef.detectChanges();

        let definition = this.pipeline.table_definition;
        if (definition) {
          this.displayColumns = removeArchivedFieldsFromTableDefinition(definition.fields, this.fieldDefs);
          this.cleanseDisplayColumns();
        }

        const currentPage = this.handleAndGetCurrentPage(pipeline.id);

        //[ch3723] - if pipeline is dynamic then retrieve saved filter
        if (this.isPipelineDynamic()) {
          if (this.pipeline.saved_filter_id) {
            this._accountService
              .getSavedFilterById(this.pipeline.saved_filter_id)
              .pipe(untilComponentDestroyed(this))
              .subscribe((savedFilter) => {
                this.savedFilter = savedFilter;
                this.queryFilters = JSON.parse(savedFilter.filters);
                this.saveQueryFilters(this.queryFilters);
                UrlUtils.saveToLocalStorage('savedFilter', this.savedFilter.id.toString(), false);
                this._projectsService
                  .getProjects(
                    this.getSelectFields(),
                    this.queryFilters,
                    currentPage,
                    this._pageSize,
                    this.getSortByArray(),
                    this.sortBy.order,
                    this.queryFilters['q'] ?? null,
                    null,
                    '',
                    this.queryFilters?.includeChildren ?? false,
                    true,
                    this.queryFilters?.includeChildren ?? false,
                    this.getNestedOptions(),
                  )
                  .pipe(untilComponentDestroyed(this), this.onGetProjectsCatchError)
                  .subscribe({ complete: () => this.onGetProjectsCompleted() });
              });
          } else {
            this._projectsService
              .getProjects(
                definition.fields.concat(definition.groupBy),
                this.queryFilters,
                currentPage,
                this._pageSize,
                this.getSortByArray(),
                this.sortBy.order,
                '',
                null,
                '',
                false,
                true,
                false,
                this.getNestedOptions(),
              )
              .pipe(untilComponentDestroyed(this), this.onGetProjectsCatchError)
              .subscribe({ complete: () => this.onGetProjectsCompleted() });
          }
        } else if (!this.filterApplied) {
          if (this.lastPipelineIdSelected !== this.data.pipeline.id) {
            if (this.pipeline.pipeline_type === 'static') this.resetQueryFilters();
          }

          this._projectsService
            .getProjects(
              definition.fields.concat(definition.groupBy),
              this.queryFilters,
              currentPage,
              this._pageSize,
              this.getSortByArray(),
              this.sortBy.order,
              this.queryFilters['q'] ?? null,
              null,
              '',
              this.queryFilters?.includeChildren ?? false,
              true,
              this.queryFilters?.includeChildren ?? false,
              this.getNestedOptions(),
            )
            .pipe(untilComponentDestroyed(this), this.onGetProjectsCatchError)
            .subscribe({ complete: () => this.onGetProjectsCompleted() });
        }
        this.isLoading = false;
      });

    this._projectsService.pipelines$.pipe(untilComponentDestroyed(this)).subscribe((pipelines: Pipeline[]) => {
      if (pipelines) {
        this.pipelines = pipelines;
      }
    });

    // Subscribe to search input field value changes
    this.searchInputControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(300),
        switchMap((query) => {
          this.isLoading = true;

          return this._projectsService.getProjects(
            this.getSelectFields(),
            this.queryFilters,
            0,
            this._pageSize,
            this.getSortByArray(),
            this.sortBy.order,
            query,
            null,
            '',
            this.queryFilters?.includeChildren ?? false,
            true,
            this.queryFilters?.includeChildren ?? false,
            this.getNestedOptions(),
          );
        }),
      )
      .pipe(untilComponentDestroyed(this), this.onGetProjectsCatchError)
      .subscribe({ complete: () => this.onGetProjectsCompleted() });

    //[sc-10736] - commented out as this was causing the subscriber to run twice.
    this.projects$ = this._projectsService.projects$;
    this.projects$.pipe(untilComponentDestroyed(this)).subscribe((projects: Project[]) => {
      if (projects) {
        this.isLoading = false;

        if (this.isLoadingChildren.find((child) => child === true)) {
          this.projects = projects;

          //clear selection boxes, so we don't get into a weird state
          this.selection.clear();
          this.groupProjects(true);
        } else {
          this.projects = projects;
          // Calculate text width of data.
          if (projects?.length > 0) {
            // Hack to calculate text width.
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            ctx.font = '12px Inter';

            // Exclude 'title' column width.
            const modColumns = this.displayColumns.slice(1);

            modColumns?.map((columnName: string, index) => {
              // Remove all empty data. as empty data don't have any width.
              const columnData = map(projects, columnName).filter((columnContent) => !!columnContent);

              // Find the width of longest data.
              const contentWidth = max(columnData.map((data) => ctx.measureText(data).width));

              // Search all column on Dom.
              const columnRef = first(
                Array.from(document.getElementsByClassName('cw-' + index) as HTMLCollectionOf<HTMLElement>),
              );
              if (!columnRef) {
                return;
              }
              let minimumWidth = 30; // Default column width

              if (contentWidth > minimumWidth) {
                // column content width is more then default width.
                minimumWidth = contentWidth;
              }
              const columnWidth = ctx.measureText(columnName?.replace(/_/gi, ' ')?.toUpperCase()).width;
              if (columnWidth > minimumWidth) {
                // column header width is more then column content width.
                minimumWidth = columnWidth;
              }

              // Should not allow column to expand more then 350px, Note: 20px right offset so content don't touch border.
              columnRef.style.minWidth = minimumWidth > 330 ? '350px' : minimumWidth + 35 + 'px';
            });
          }
          this.projectsCount = projects.length;

          //clear selection boxes, so we don't get into a weird state
          this.selection.clear();
          this.groupProjects();
        }
      }

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    // Get the pagination

    this._projectsService.pagination$.pipe(untilComponentDestroyed(this)).subscribe((pagination: ProjectPagination) => {
      if (pagination) {
        // Update the pagination
        this.pagination = pagination;
        this.expandedProjects = [];
        this.getColDefs();
        // Mark for check
        this._changeDetectorRef.markForCheck();
      }
    });

    UrlUtils.saveToLocalStorage('projectView', 'list');

    this.projectTypes = this.data.options.project_types.filter((option) => !option.hidden);
    this.propertyTypes = this.data.options.property_types.filter((option) => !option.hidden);
    this.projectStages = this.data.options.project_stages.filter((option) => !option.hidden);
    this.projectPhases = this.data.options.project_phases.filter((option) => !option.hidden);
    this.investmentTypes = this.data.options.investment_types.filter((option) => !option.hidden);
    this.vehicles = this.data.options.vehicles.filter((option) => !option.hidden);
    this.users = this.data.options.users;

    this.filterForm = this._formBuilder.group({
      propertyTypeIdIn: [''],
      projectTypeIdIn: [''],
      vehicleIdIn: [''],
      assignedUserIdIn: [''],
      investmentTypeIdIn: [''],
      projectStageIdIn: [''],
      createdAtGteq: [''],
      createdAtLteq: [''],
      city: [''],
      marketNameCont: [''],
      zip: [''],
      state: [''],
    });

    this.clearTableScrollPosition();

    this.getColDefs();

    this.columnResizeSubject.pipe(debounceTime(1000)).subscribe(() => {
      const columnState = this.gridApi.getColumnState();
      this.gridApi.applyColumnState({ state: columnState });
      this._saveColumns();
    });
  }

  ngAfterViewChecked(): void {
    this.isLoading$.pipe(untilComponentDestroyed(this)).subscribe((isLoading) => {
      this.gridOptions.loading = isLoading;
    });
    if (this.isDataUpdating) {
      this.setTableScrollPosition();
      this.isDataUpdating = false;
    }
  }

  /**
   * Clear table scroll position reference when navigated away from the page.
   * Cover the case when user switch views.
   */
  private clearTableScrollPosition(): void {
    this._router.events.pipe(untilComponentDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationStart) this.resetScrollPosition = true;
    });
  }

  // After table data refresh, apply previous scroll position.
  private setTableScrollPosition(): void {
    if (this.resetScrollPosition) {
      this.tableWrapperElement.scrollLeft = 0;
      this.tableWrapperElement.scrollTop = 0;
      this.onEditStart();
      this.resetScrollPosition = !this.resetScrollPosition;
    } else {
      setTimeout(() => {
        this.tableWrapperElement.scrollLeft = this.tableScrollPosition.left;
        this.tableWrapperElement.scrollTop = this.tableScrollPosition.top;
      }, 1000);
    }
  }

  // Get table left/top scroll position before table data refresh.
  private saveTableScrollPosition(): void {
    this.tableScrollPosition.left = this.tableWrapperElement.scrollLeft;
    this.tableScrollPosition.top = this.tableWrapperElement.scrollTop;
  }

  private onEditStart(): void {
    this.saveTableScrollPosition();
  }

  private onEditEnd(): void {
    this.isDataUpdating = true;
  }

  // Don't hide child deals from table when user toggles 'include children' in filter panel.
  public checkHiddenChildren(project: Project): boolean {
    return this.includeChildrenToggled ? false : !this.expandedProjects.includes(this.parser(project.parent_id));
  }

  private currentGroupByCollumnIsArchivedField(groupBy: string, fieldDefs: FieldDef[]) {
    return !fieldDefs.find((fieldDef) => fieldDef.name.toLowerCase() === groupBy.toLowerCase());
  }

  public newGroupByKey(groupByField: string): string {
    const groupByFieldMap = {
      investment_type: 'investment_type_id',
      project_phase: 'project_phase_id',
      property_type: 'property_type_id',
      state: 'state',
      project_stage: 'project_stage_id',
      team: 'team_id',
      assigned_user: 'assigned_user_id',
      vehicle: 'vehicle_id',
    };

    return groupByFieldMap[groupByField];
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.projects?.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.projects.forEach(({ id }: Project) => this.selection.select(Number(id)));
  }

  toggleProject(project: Project): void {
    this.selection.toggle(Number(project.id));
  }

  isProjectSelected(project: Project): boolean {
    return this.selection.isSelected(Number(project.id));
  }

  public groupFieldValue(fieldKeyValue: string | undefined): string {
    if (!fieldKeyValue) {
      return '';
    }
    return fieldKeyValue.replace('_id', '');
  }

  /**
   * Group projects by a field
   */
  groupProjects(_isLoadingChildren = false): void {
    this.groupedProjects = [];
    let groupBy = this.pipeline.table_definition?.groupBy || this.defaultGroupBy;
    let groupedBy = (groupBy = this.groupFieldValue(groupBy));
    let optionsGroupedBy = null;
    const OTHER = 'Other';

    let sortedGroupKeys = [];
    let visibleOptions: ProjectStage[] = [];

    if (groupedBy != 'state') {
      groupedBy = `${groupBy}.name`;
      optionsGroupedBy = plural(groupBy);
      if (groupBy == 'assigned_user') {
        let sortedOptions = sortBy(this.data.options[optionsGroupedBy], 'name');
        if (!sortedOptions?.length) {
          sortedOptions = sortBy(this.data.options['users'], 'rank');
        }
        sortedGroupKeys = sortedOptions.map((option) => option.name || OTHER);
        sortedGroupKeys = sortedGroupKeys.filter((elem, index, self) => {
          return index === self.indexOf(elem);
        });
      } else if (groupBy == 'project_stage') {
        visibleOptions = this.isPipelineDynamic()
          ? this.data.options[optionsGroupedBy]
          : this.data.options[optionsGroupedBy].filter((option) => option?.pipeline_ids?.includes(this.pipeline.id));
        let sortedOptions = sortBy(visibleOptions, 'rank');
        sortedGroupKeys = sortedOptions.map((option) => option.name || OTHER);
        if (!sortedGroupKeys.includes(OTHER)) {
          sortedGroupKeys.push(OTHER);
        }
        sortedGroupKeys = sortedGroupKeys.filter((elem, index, self) => {
          return index === self.indexOf(elem);
        });
      } else {
        let sortedOptions = sortBy(this.data.options[optionsGroupedBy], 'rank');
        sortedGroupKeys = sortedOptions.map((option) => option.name || OTHER);
        sortedGroupKeys = sortedGroupKeys.filter((elem, index, self) => {
          return index === self.indexOf(elem);
        });
      }
    }

    if (!sortedGroupKeys.length) {
      groupedBy = groupedBy.split('.')[0];
      sortedGroupKeys = this.projects
        .map((project) => get(project, groupedBy))
        .reduce((prev, curr) => {
          let stg;

          if (Array.isArray(curr)) {
            stg = curr.join(',') || OTHER;
          } else {
            if (curr && typeof curr === 'object' && Object.keys(curr).length) {
              stg = curr.name;
            } else {
              stg = curr === null || curr === undefined ? OTHER : curr;
            }
          }

          return !prev.includes(stg) ? [...prev, stg] : prev;
        }, []);
    }

    let groupings = this.includeChildrenToggled
      ? _groupBy(this.projects, (b) => get(b, groupedBy, OTHER))
      : _groupBy(
          this.projects.filter((p) => !p.is_child || p.is_child === 'false'),
          (b) => {
            const a = get(b, groupedBy);
            if (a && typeof a === 'object' && !Array.isArray(a) && Object.keys(a).length) {
              return a.name;
            }
            return get(b, groupedBy, OTHER);
          },
        );

    // Handle deal statuses groups that are not associated with the pipeline
    if (groupBy == 'project_stage') {
      Object.keys(groupings).forEach((key) => {
        if (key !== OTHER && !visibleOptions.some((option) => option.name === key)) {
          groupings[OTHER] = [...(groupings[OTHER] || []), ...groupings[key]];
          delete groupings[key];
        }
      });
    }

    if (groupings['null']) {
      groupings[OTHER] = [...groupings['null'], ...(groupings[OTHER] || [])];
      delete groupings['null'];
    }

    if (!sortedGroupKeys || sortedGroupKeys.length <= 0) {
      sortedGroupKeys = keys(groupings);
    }

    if (groupings['']) {
      groupings[OTHER] = [...groupings[''], ...(groupings[OTHER] || [])];
      delete groupings[''];
    }

    if (!sortedGroupKeys.find((key) => key === OTHER)) {
      sortedGroupKeys.push(OTHER);
    }

    for (let key of sortedGroupKeys) {
      if (!key) {
        key = OTHER;
      }
      if (!has(groupings, key)) {
        continue;
      }

      let vals = groupings[key];
      let group = new Group();
      group.fieldName = groupedBy;
      group.label = key.split('|')[1] || key;
      group.totalCount = this.includeChildrenToggled
        ? vals.length
        : vals.filter((item) => item.is_child !== 'true').length;
      //now lets get the sums
      let sums = {};
      let averages = {};
      let weightedAverages = {};
      let nonZeroValCount = {};
      // eslint-disable-next-line @typescript-eslint/naming-convention
      let _this = this;
      let columns = this.loopDisplayColumns;

      let newVals = clone(vals);

      newVals.forEach((proj) => {
        if (proj.is_parent === 'true') {
          let children = this.includeChildrenToggled
            ? this.projects
            : this.projects.filter((p) => p.is_child === 'true');
          children.forEach((child) => {
            if (String(child.parent_id) == proj.id) {
              let insertIndex = vals.findIndex((item) => item.id == proj.id);
              insertIndex++;
              vals.splice(insertIndex, 0, child);
            }
          });
        }
      });

      each(vals, function (item) {
        each(columns, function (column) {
          let fieldDef = _this.lookupField(column);
          if (
            fieldDef.type == 'money' ||
            fieldDef.type == 'percent' ||
            fieldDef.type == 'number' ||
            fieldDef.type == 'decimal'
          ) {
            if (item[column] && (!item.is_child || item.is_child === 'false')) {
              sums[`group_${column}`] = (sums[`group_${column}`] || 0) + parseFloat(item[column]);
              nonZeroValCount[`group_${column}`] = nonZeroValCount[`group_${column}`] || 0;
              if (parseFloat(item[column]) != 0) {
                nonZeroValCount[`group_${column}`] += 1;
              }
              if (fieldDef.type == 'percent' && item['equity']) {
                weightedAverages[`group_${column}`] =
                  (weightedAverages[`group_${column}`] || 0) + (parseFloat(item[column]) / 100) * item['equity'];
              } else if (fieldDef.type == 'percent' && item['cf_cash_equity_2']) {
                weightedAverages[`group_${column}`] =
                  (weightedAverages[`group_${column}`] || 0) +
                  (parseFloat(item[column]) / 100) * item['cf_cash_equity_2'];
              }
            }
          }
        });
      });

      for (let sumColumn in sums) {
        let sumVal = sums[sumColumn];

        if (nonZeroValCount[sumColumn] && nonZeroValCount[sumColumn] > 0) {
          averages[sumColumn] = sumVal / nonZeroValCount[sumColumn];
        }
        group.summaryColumns[sumColumn] = {};
        group.summaryColumns[sumColumn]['total'] = sums[sumColumn];
        group.summaryColumns[sumColumn]['average'] = averages[sumColumn];
        if (weightedAverages[sumColumn] && sums['group_equity']) {
          group.summaryColumns[sumColumn]['weighted_average'] =
            (weightedAverages[sumColumn] / sums['group_equity']) * 100;
        } else if (weightedAverages[sumColumn] && sums['group_cf_cash_equity_2']) {
          group.summaryColumns[sumColumn]['weighted_average'] =
            (weightedAverages[sumColumn] / sums['group_cf_cash_equity_2']) * 100;
        }
      }

      if (group.totalCount > 0) {
        this.groupedProjects.push(group);
        this.groupedProjects = [...this.groupedProjects, ...vals];
      }
    }

    if (!_isLoadingChildren) {
      this._renderArrayChunksOverTime(this.groupedProjects);
      this.loadRowData();
    } else {
      this.chunkedProjectsArray = this.groupedProjects;
      this.loadRowData();

      this.onEditEnd();
    }
  }

  mergeIntoPortfolio(): void {
    let deals: any[] = this.projects
      .filter((x) => this.selection.selected.includes(+x.id))
      .map(({ id, is_parent, parent_id, title }) => ({ id, parent_id, is_parent: is_parent === 'true', title }));

    // deals = deals
    //   .map((x) => ({ ...x, parentSelected: deals.find((y) => y.id == x.parent_id) }))
    //   .filter((x) => !x.parentSelected);

    this._matDialog
      .open(PortfolioMergeComponent, {
        panelClass: 'mailbox-compose-dialog',
        disableClose: true,
        data: {
          destination_portfolio_pipeline_id: this.pipeline.id,
          deals,
          templates: this._projectTemplates,
          pipelines: this.pipelines,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result) => result),
        tap(() => this.reloadProjects()),
      )
      .subscribe(noop);
  }

  /**
   * @param tableListProjects array of objects representing groups and projects
   * @param chunkSize value for the size of sliced array chunks, defaults to 20.
   * @param timeDelay time period value for chunks to be rendered, defaults to 250ms.
   * @returns an array with chunks of the same array of objects being populated over time.
   */
  private _renderArrayChunksOverTime(
    tableListProjects: Array<ProjectGroupOrProject>,
    chunkSize: number = 20,
    timeDelay: number = 250,
  ): Array<ProjectGroupOrProject> {
    if (this._pageSize === 100) {
      if (this.refreshMode === 'Group') {
        tableListProjects
          .filter((groupOrProject: ProjectGroup) => groupOrProject.summaryColumns)
          ?.forEach((group: ProjectGroup) => {
            const actualGroup = this.chunkedProjectsArray.find(
              ({ label }: ProjectGroup) => label === group.label,
            ) as ProjectGroup;

            if (actualGroup) {
              actualGroup.summaryColumns = group.summaryColumns;
            }
          });
      } else {
        this.chunkedProjectsArray = [];
        let tempChunkHolderArray: Array<ProjectGroupOrProject> = [];
        const chunks = [];
        tableListProjects.forEach((project, index) => {
          tempChunkHolderArray.push(tableListProjects[index]);
          if (index % chunkSize === 0) {
            chunks.push(tempChunkHolderArray);
            tempChunkHolderArray = [];
          }
        });
        chunks.push(tempChunkHolderArray);
        chunks.forEach((item, index) => {
          setTimeout(
            () => {
              this.chunkedProjectsArray = [...this.chunkedProjectsArray, ...item];
              this._changeDetectorRef.detectChanges();
            },
            index === 0 ? 0 : timeDelay,
          );
        });
      }
    } else {
      return (this.chunkedProjectsArray = tableListProjects);
    }

    this.onEditEnd();
    this.refreshMode = 'All';
  }

  public isGroup(index, item): boolean {
    return item.totalCount;
  }

  isProjectGroup(row: ProjectGroupOrProject): row is ProjectGroup {
    return (row as ProjectGroup).fieldName !== undefined;
  }

  /****** BEGIN PORTFOLIOS */
  public isProject(index, item: Project): boolean {
    return item.id && item?.is_child !== 'true';
  }

  public isParent(item: Project): boolean {
    return item?.is_parent === 'true';
  }

  public isChild(index, item: Project): boolean {
    return item?.is_child === 'true';
  }

  public expand(project: Project, projectPosition: number): void {
    this.onEditStart();
    let id = parseInt(project.id);
    if (this.expandedProjects.includes(id)) {
      let index = this.expandedProjects.indexOf(id);
      this.expandedProjects.splice(index, 1);
    } else {
      this.expandedProjects.push(id);
      this._fetchChildrenProject(project, projectPosition);
    }
    this.onEditEnd();
  }

  private _fetchChildrenProject(project: any, index: number): void {
    if (!project.loaded) {
      this.isLoadingChildren[index] = true;
      project.loaded = true;
      this._projectsService
        .getProjects(
          this.getSelectFields().concat(['parent_id']),
          { fjson: `[["parent_id","eq","${project.id}"]]` },
          0,
          100,
          this.getSortByArray(),
          this.sortBy.order,
          '',
          null,
          '',
          true,
          true,
          false,
          this.getNestedOptions(),
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe({
          next: () => (this.isLoadingChildren[index] = false),
        });
    }
  }

  expanded(project) {
    let expander = this.expandedProjects?.includes(parseInt(project?.id));
    return expander;
  }

  duplicateColName(col) {
    return col + '2';
  }

  parentColumns() {
    let parentColumns = [];
    this.displayColumns.forEach((col) => {
      parentColumns.push(col + '2');
    });
    parentColumns.unshift('icon2');
    return parentColumns;
  }

  parser(val) {
    return parseInt(val);
  }

  /****** END PORTFOLIOS */

  /**
   * Given a grouped field name, replace the group_ and get the fieldDef
   * @param groupedColumn
   */
  lookupGroupSummaryField(groupedColumn) {
    let fieldDef = this.lookupField(groupedColumn.replace('group_', ''));

    if (fieldDef) {
      let groupedTotalAverages = this.getGroupedTotalAverageForField(fieldDef);
      if (groupedTotalAverages) {
        fieldDef.displayGroupedType = groupedTotalAverages;
      }
    }
    return fieldDef;
  }

  cleanseDisplayColumns(): void {
    if (!this.newTable) {
      const index = this.displayColumns.findIndex((item) => item === 'title');
      // Delete the label
      this.displayColumns.splice(index, 1);
      this.loopDisplayColumns = clone(this.displayColumns);
      this.displayColumns.unshift('title');
      this.tableDisplayColumns = clone(this.displayColumns);
    } else {
      this.loopDisplayColumns = this.displayColumns.slice(1);
      this.tableDisplayColumns = ['icon', ...this.displayColumns.slice(1)];
    }

    this.groupedSummaryColumns = ['group_header'];
    for (let column of this.loopDisplayColumns) {
      this.groupedSummaryColumns.push(`group_${column}`);
    }
  }

  getGroupedSummaryColumns() {
    return this.groupedSummaryColumns.filter((item) => item !== 'group_header');
  }

  toggleGroupedTotalAverages(displayType: string, field: FieldDef): void {
    field.displayGroupedType = displayType;
    UrlUtils.saveToLocalStorage('Project_' + field.name, displayType);
  }

  getGroupedTotalAverageForField(fieldDef: FieldDef): string {
    return UrlUtils.getFromLocalStorage('Project_' + fieldDef.name, null);
  }

  ngAfterViewInit(): void {
    if (!this.newTable) {
      this._sort.sortChange.pipe(untilComponentDestroyed(this)).subscribe(() => {
        //reset back to first page
        this._paginator.pageIndex = 0;
      });
    }
    merge(this.newTable ? this.sortChangedSubject : this._sort.sortChange, this._paginator.page)
      .pipe(
        switchMap(() => {
          return this.getProjectsBySort();
        }),
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => (this.isLoading = false));
  }

  getProjectsBySort() {
    this.isLoading = true;
    sessionStorage.setItem('currentPage', String(this._paginator.pageIndex));
    let sortField = this.newTable ? this._sortField : this._sort.active;

    let field = this.lookupField(sortField);
    if (field) {
      if (field.meta && field.meta.sorting) {
        sortField = field.meta.sorting;
      }
    }
    this.savePageSize();
    this.sortBy = { field: sortField, order: this.newTable ? this._sortDirection : this._sort.direction };
    return this._projectsService.getProjects(
      this.getSelectFields(),
      this.queryFilters,
      this._paginator.pageIndex,
      this._paginator.pageSize,
      this.getSortByArray(),
      this.sortBy.order,
      this.queryFilters?.q ?? '',
      null,
      '',
      this.queryFilters?.includeChildren ?? false,
      true,
      this.queryFilters?.includeChildren ?? false,
      this.getNestedOptions(),
    );
  }

  exportProjects(format: string = 'csv', addChildren: boolean): void {
    if (format === FileFormat.CSV) {
      this.exportingCsv = true;
    } else if (format === FileFormat.PDF) {
      this.exportingPdf = true;
    }
    let sortField = this.newTable ? this._sortField : this._sort.active;

    let field = this.lookupField(sortField);
    if (field) {
      if (field.meta && field.meta.sorting) {
        sortField = field.meta.sorting;
      }
    }
    let query = this.searchInputControl.value;

    const groupBy = this.pipeline.table_definition.groupBy || this.defaultGroupBy;
    let group = `${groupBy}_id`;

    //set max page size for download
    let maxPageSize = 1000;

    this._projectsService
      .getProjects(
        this.getSelectFields(),
        this.queryFilters,
        0,
        maxPageSize,
        sortField,
        this.newTable ? this._sortDirection : this._sort.direction,
        query,
        format as FileFormat,
        group,
        addChildren,
        true,
        addChildren,
        this.getNestedOptions(),
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        if (data) {
          if ([FileFormat.PDF, FileFormat.XLSX, FileFormat.CSV].includes(format as FileFormat)) {
            if (data?.message) {
              this._snackBar.open(data?.message, 'info', null, {
                duration: 3000,
              });
            } else {
              generateDownloadableFile(data, format as FileFormat, `${this.pipeline.name}'s deals`);
            }
          }

          if (format === FileFormat.CSV) {
            this.exportingCsv = false;
          } else if (format === FileFormat.PDF) {
            this.exportingPdf = false;
          }

          this._changeDetectorRef.markForCheck();
        }
      });
  }

  onUpdate(params: any, project: Project): void {
    this.onEditStart();

    const options = { ...params, id: project.id };
    const selectColumn = this.defaultFieldDefs.map((m) => m.name) || [];
    const paramsKeys = Object.keys(params);
    const [firstParamsKey] = paramsKeys;

    selectColumn.push(firstParamsKey);

    if (!this.newTable) this.isLoading = true;

    if (firstParamsKey === 'add_new') {
      this._projectsService
        .getProjects(
          this.getSelectFields(),
          this.queryFilters,
          0,
          this._pageSize,
          this.getSortByArray(),
          this.sortBy.order,
          this.queryFilters?.q ?? '',
          null,
          '',
          this.queryFilters?.includeChildren ?? false,
          true,
          this.queryFilters?.includeChildren ?? false,
          this.getNestedOptions(),
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe({
          next: () => (this.isLoading = false),
          error: () => (this.isLoading = false),
        });
    } else {
      if (paramsKeys.includes('project_stage_id')) {
        const fullProject = this.projects.find(({ id }: Project) => id == project.id);
        this.updateProjectStatus
          .update(this.data, fullProject, {
            id: +project.id,
            project_stage_id: params.project_stage_id,
          })
          .pipe(untilComponentDestroyed(this))
          .subscribe({
            next: (result) => {
              this.reloadProjects();
              if (!result) {
                if (!this.newTable) {
                  this._sort.sort({
                    id: 'status',
                    start: 'asc',
                    disableClear: true,
                  });
                }
              }
              this.isLoading = false;
            },
            error: () => (this.isLoading = false),
          });
      } else if (paramsKeys.includes('project_phase_id')) {
        const phase = this.projectPhases.find(({ id }: ProjectPhase) => id === Number(params.project_phase_id));
        const hasRequiredFields = this.handleSoftRequiredFields(phase, project.id, false);
        if (!hasRequiredFields) {
          const handleError = (error: any) => {
            const errorMessage = error?.error?.project?.errors?.join('. ');
            this._snackBar.open(errorMessage, 'error', null, { duration: 5000 });
          };

          this._projectsService
            .saveProject(options, project, false)
            .pipe(
              untilComponentDestroyed(this),
              catchError((error) => {
                handleError(error);
                return throwError(() => new Error(error));
              }),
              switchMap(() =>
                this._projectsService.getProjects(
                  this.getSelectFields(),
                  this.queryFilters,
                  this.pagination.page,
                  this._pageSize,
                  this.getSortByArray(),
                  this.sortBy.order,
                  this.queryFilters?.q ?? '',
                  null,
                  '',
                  this.queryFilters?.includeChildren ?? false,
                  true,
                  this.queryFilters?.includeChildren ?? false,
                  this.getNestedOptions(),
                ),
              ),
              untilComponentDestroyed(this),
              finalize(() => (this.isLoading = false)),
            )
            .subscribe(noop);
        }
      } else {
        this._projectsService
          .saveProject(options, project, false)
          .pipe(
            untilComponentDestroyed(this),
            catchError((error) => {
              const errorMessage = error?.error?.project?.errors?.join('. ');
              this._snackBar.open(errorMessage, 'error', null, { duration: 5000 });
              return throwError(() => error);
            }),
          )
          .subscribe((project) => {
            const index = this.chunkedProjectsArray.findIndex((item: Project) => item.id == project.id);
            if (index > -1) {
              this.chunkedProjectsArray[index] = { ...this.chunkedProjectsArray[index], ...project };
              this.chunkedProjectsArray = [...this.chunkedProjectsArray];
            }
            this.isLoading = false;
          });
      }

      this.refreshMode = 'Group';
    }

    this.onEditEnd();
  }

  lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  /**
   * Create project
   */
  createProject(): void {
    // Open the dialog
    const dialogRef = this._matDialog.open(ProjectAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: this.data,
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        //if project saved
        if (result && result.project) {
          //RRS: TODO, redirect to deal directly
          this.openProjectDialog(result.project);
          //this._router.navigate(['../', result.project.id], {relativeTo: this._activatedRoute});
        }
      });
  }

  onProjectFileChanged(event) {
    let file = event.target.files[0];
    this.uploadingFile = true;
    this._projectsService
      .uploadOm(file)
      .pipe(untilComponentDestroyed(this))
      .subscribe((document) => {
        this.uploadingFile = false;
        this._router.navigate(['/projects', this.pipeline.id, 'pdf'], {
          queryParams: { pdf_url: encodeURIComponent(document.pdf_url), document_id: document.id },
        });
      });
  }

  navigateToProjectsAddMap(): void {
    this._router.navigate(['/projects', this.pipeline.id, 'map', 'add']);
  }

  /**
   * Save query filters to cache
   */
  saveQueryFilters(filters: any): void {
    UrlUtils.saveToLocalStorage('Project_queryFilters', filters, true);
  }

  /**
   * Given a queryFilter save to localStorage
   */
  fillQueryFilters(): void {
    this.queryFilters = UrlUtils.getFromLocalStorage('Project_queryFilters', true);
    if (this.queryFilters && this.filterForm) {
      this.filterForm.patchValue(this.queryFilters);
    }
  }

  fillSortBy(): void {
    let sortBy = UrlUtils.getFromLocalStorage('sortBy');
    if (sortBy) {
      this.sortBy = JSON.parse(sortBy);
      if (this.sortBy.field == 'project_stages.rank') {
        this.sortBy.field = 'project_stage_rank';
        this.sortBy.order = 'asc';
      }
    } else {
      this.sortBy = { field: 'project_stage_rank', order: 'asc' };
    }
  }

  getSortByArray() {
    const groupBy = this.pipeline.table_definition.groupBy || this.defaultGroupBy;
    const sort = this.filteredFieldDefs.find(({ name }) => name === groupBy)?.meta?.sorting ?? groupBy;

    let arr = [];
    arr.push({ field: sort, order: 'asc' });
    if (sort != this.sortBy.field) {
      arr.push({ field: this.sortBy.field, order: this.sortBy.order });
    } else {
      arr.push({ field: 'created_at', order: 'desc' });
    }
    return arr;
  }

  savePageSize(): void {
    UrlUtils.saveToLocalStorage('pageSize', this._paginator.pageSize.toString());
  }

  fillPageSize(): void {
    let pageSize = UrlUtils.getFromLocalStorage('pageSize');
    if (pageSize) {
      if (!this._pageSizeOptions.includes(parseInt(pageSize))) {
        pageSize = this._defaultPageSize;
        UrlUtils.saveToLocalStorage('pageSize', pageSize);
      }
      this._pageSize = parseInt(pageSize);
    }
  }

  openFilterDialog(): void {
    const dialogRef = this._matDialog.open(ProjectsFilterComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, filters: this.queryFilters, savedFilter: this.savedFilter },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.filters) {
          this.queryFilters = result.filters;
          this._projectsService
            .getProjects(
              this.getSelectFields(),
              this.queryFilters,
              0,
              10,
              this.getSortByArray(),
              this.sortBy.order,
              '',
              null,
              '',
              false,
              true,
              false,
              this.getNestedOptions(),
            )
            .pipe(untilComponentDestroyed(this))
            .subscribe();
        } else {
          let url = this._router.url.split('?')[0];
          this._router.navigate([url], { queryParams: { filterId: 'reset' } });
        }
      });
  }

  openFilterSaveDialog(): void {
    this._matDialog.open(ProjectsFilterSaveComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, filters: this.queryFilters, savedFilter: this.savedFilter },
    });
  }

  ngOnDestroy(): void {
    // Dispose the overlays if they are still on the DOM
    if (this._fieldDefsPanelOverlayRef) {
      this._fieldDefsPanelOverlayRef.dispose();
    }

    if (this._customFieldPanelOverlayRef) {
      this._customFieldPanelOverlayRef.dispose();
    }

    this.resetQueryFilters();

    this._projectsService.bustPipelineCache();

    super.ngOnDestroy();
  }

  deleteProject(project: Project): void {
    this.confirmService
      .openConfirmationModal({ message: `Are you sure you would like to delete ${project.title}?` })
      .subscribe((result) => {
        // Get the current contact's id
        const id = project.id;

        // Delete the contact
        this._projectsService
          .deleteProject(id)
          .pipe(untilComponentDestroyed(this))
          .subscribe((isDeleted) => {
            // Mark for check
            this._paginator._changePageSize(this._paginator.pageSize);
            this._changeDetectorRef.markForCheck();
          });
      });
  }

  /**
   * Update status of selected projects
   */
  changeStatusOfSelected() {
    if (!this.canEditAllSelectedDeals()) {
      return;
    }

    const dialogRef = this._matDialog.open(ProjectChangeStatusComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        projects: this.selectedProjects,
        pipelines: this.pipelines,
        data: this.data,
        pipeline: this.pipeline,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.updated) {
          this._snackBar.open('Deal has been updated.', 'success');
          this.selection.clear();
          this._paginator._changePageSize(this._paginator.pageSize);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  moveSelectedDeals() {
    if (!this.canEditAllSelectedDeals()) {
      return;
    }
    const dialogRef = this._matDialog.open(ProjectEditPipelineComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        projects: this.selectedProjects,
        pipelines: this.pipelines,
        data: this.data,
        currentPipeline: this.pipeline,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result && (result.moved || result.updated)),
        tap(() => {
          this.isLoading = true;
          this._changeDetectorRef.markForCheck();
          this._projectsService.bustPipelineCache();
        }),
        switchMap(() => this._projectsService.getPipelines()),
        tap(() => {
          this._paginator._changePageSize(this._paginator.pageSize);
          this._snackBar.open('Deal has been moved.', 'success');
          this.selection.clear();
        }),
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  changeSelectedLayouts() {
    const dialogRef = this._matDialog.open(ProjectChangeLayoutsComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { projects: this.selectedProjects, pipelines: this.pipelines, data: this.data },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.moved) {
          this._snackBar.open('Deal has been updated.', 'success');
          this.selection.clear();
          this._paginator._changePageSize(this._paginator.pageSize);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  deleteSelectedProjects(): void {
    if (!this.canDeleteAllSelectedDeals()) {
      return;
    }

    const someProjectIsParent = this.selectedProjects.some((obj) => obj.is_parent === 'true');

    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you would like to delete the selected deals? ${
          someProjectIsParent ? '\nThis will delete all related child deals as well.' : ''
        }`,
      })
      .subscribe((res) => {
        if (!res) return;
        for (const project of this.selectedProjects) {
          this._projectsService
            .deleteProject(project.id)
            .pipe(untilComponentDestroyed(this))
            .subscribe(() => {
              this._paginator._changePageSize(this._paginator.pageSize);
              this._changeDetectorRef.markForCheck();
            });
        }
      });

    return;
  }

  openProjectDialog(project: Project): void {
    this._router.navigate(['', { outlets: { modal: ['pd', this.pipeline.id, project.id] } }]);
  }

  /**
   * When change the group by drop down
   * @param event
   */
  onChangeGroupBy(event: MatSelectChange): void {
    this.resetScrollPosition = true;
    this.isLoading = true;
    this.openTrackStateSnackbar('groupBy');
    const groupBy = event.value;

    this.pipeline.table_definition = {
      ...this.pipeline.table_definition,
      fields: this.displayColumns,
      groupBy: groupBy,
    };

    this._projectsService
      .getProjects(
        this.getSelectFields(),
        this.queryFilters,
        0,
        this._pageSize,
        this.getSortByArray(),
        this.sortBy.order,
        this.queryFilters['q'] ?? null,
        null,
        '',
        this.queryFilters.includeChildren ?? false,
        true,
        this.queryFilters.includeChildren ?? false,
        this.getNestedOptions(),
      )
      .pipe(untilComponentDestroyed(this), this.onGetProjectsCatchError)
      .subscribe({ complete: () => this.onGetProjectsCompleted() });
  }

  private getSelectFields(): string[] {
    const groupByField =
      typeof this.pipeline.table_definition === 'string'
        ? JSON.parse(this.pipeline.table_definition ?? '{}')?.groupBy
        : this.pipeline.table_definition?.groupBy;
    return [groupByField || this.defaultGroupBy, ...this.displayColumns];
  }

  droppedFilterDef($event: CdkDragDrop<any[]>): void {
    const from: number = $event.previousIndex;
    const to: number = $event.currentIndex;

    if (to != 0 && from != 0) {
      if (to !== from) {
        moveItemInArray(this.displayColumns, from, to);
      }
    }
  }

  /**
   * Display Column dropped
   *
   * @param event
   */
  dropped(event: CdkDragDrop<any[]>): void {
    // Move the item in the array
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Toggle the tags edit mode
   */
  toggleEditDisplayColumnsModel(): void {
    if (this.displayColumnsEditMode) {
      this._saveColumns();
    }
    this.displayColumnsEditMode = !this.displayColumnsEditMode;
  }

  /**
   * Should the create tag button be visible
   *
   * @param inputValue
   */
  shouldShowCreateCustomFieldButton(inputValue: string): boolean {
    return !(
      inputValue === '' ||
      this.fieldDefs.findIndex((fieldDef) => fieldDef.label.toLowerCase().includes(inputValue.toLowerCase())) > -1
    );
  }

  /**
   * Filter tags
   *
   * @param event
   */
  filterFields(event): void {
    // Get the value
    const value = event.target.value.toLowerCase();
    this.fieldSearchValue = value;

    // Filter the tags
    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.label.toLowerCase().includes(value));
  }

  /**
   * Filter tags input key down event
   *
   * @param event
   */
  filterFieldsInputKeyDown(event): void {
    // Return, if the pressed key is not 'Enter'
    if (event.key !== 'Enter') {
      return;
    }

    // If there is a tag...
    const fieldDef = this.filteredFieldDefs[0];

    if (!fieldDef) {
      this.openCustomFieldPanel(event.target.value);
      return;
    }

    const isFieldVisible = this.displayColumns.find((item) => item === fieldDef.name);

    // If the found tag is already applied to the contact...
    if (isFieldVisible) {
      // Remove the tag from the contact
      this.removeDisplayColumn(fieldDef.name);
    } else {
      // Otherwise add the tag to the contact
      this.addDisplayColumn(fieldDef.name);
    }
  }

  /**
   * Toggle display column
   *
   * @param fieldName
   * @param change
   */
  toggleDisplayColumn(fieldName: any, change: MatCheckboxChange): void {
    this.openTrackStateSnackbar('columns');
    if (change.checked) {
      this.addDisplayColumn(fieldName);
    } else {
      this.removeDisplayColumn(fieldName);
    }
  }

  /**
   * Remove tag from the contact
   */
  removeDisplayColumn(fieldName: string): void {
    const index = this.displayColumns.findIndex((item) => item === fieldName);
    if (index > -1) {
      this.displayColumns.splice(index, 1);
    }
    if (this.newTable) {
      const col = this.columnDefs.find((col) => col.name === fieldName);
      if (col) {
        col.tsTableOptions = null;
      }
    }

    this.cleanseDisplayColumns();
    this.getColDefs();
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Add tag to the contact
   */
  addDisplayColumn(fieldName: string, saveColumnOnApi = true): void {
    // Add the tag
    //check if the field already exists before adding it back.
    //we don't want duplicate fields in the table_definition
    //this should be -1 in cases where the field is not found
    let index = this.displayColumns.findIndex((item) => item === fieldName);
    if (index === -1) {
      if (this.displayColumns.length > 0) {
        this.displayColumns.splice(1, 0, fieldName);
      } else {
        this.displayColumns.push(fieldName);
      }
      this.cleanseDisplayColumns();
      this.getColDefs();
      this._changeDetectorRef.markForCheck();
      //save fields to pipeline
      //SC-38806, commenting since we should only save the columns once the layout is saved
      // if (saveColumnOnApi) this.saveColumnSubject$.next();
    }
  }

  openFieldDefsPanel(): void {
    for (let column of this.displayColumns?.slice()?.reverse()) {
      this.fieldDefs = this.moveToFirst(column, this.fieldDefs);
    }
    // Create the overlay
    this._fieldDefsPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._fieldDefsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._fieldDefsPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        // Focus to the search input once the overlay has been attached
        this._fieldDefsPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    // Create a portal from the template
    this._fieldsDefsPanelTemplatePortal = new TemplatePortal(this._fieldDefsPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._fieldDefsPanelOverlayRef.attach(this._fieldsDefsPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._fieldDefsPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._closeDisplayFieldsPanel();
      });
  }

  /**
   * Close the event panel
   */
  private _closeDisplayFieldsPanel(): void {
    // If overlay exists and attached...
    if (this._fieldDefsPanelOverlayRef && this._fieldDefsPanelOverlayRef.hasAttached()) {
      // Detach it
      this._fieldDefsPanelOverlayRef.detach();

      // Reset the tag filter
      this.filteredFieldDefs = this.fieldDefs;

      // Toggle the edit mode off
      this.displayColumnsEditMode = false;
    }

    // If template portal exists and attached...
    if (this._fieldsDefsPanelTemplatePortal && this._fieldsDefsPanelTemplatePortal.isAttached) {
      // Detach it
      this._fieldsDefsPanelTemplatePortal.detach();
    }
  }

  openCustomFieldPanel(fieldLabel?: string): void {
    // Create the overlay
    this._customFieldPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._customFieldPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._customFieldPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        // Focus to the search input once the overlay has been attached
        this._customFieldPanelOverlayRef.overlayElement.querySelector('input').focus();

        //We know that if a field label is provided, then this request came from the display columns ui widget.
        if (fieldLabel) {
          this._closeDisplayFieldsPanel();
        }
      });

    // Create a portal from the template
    this._customFieldPanelTemplatePortal = new TemplatePortal(this._customFieldPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._customFieldPanelOverlayRef.attach(this._customFieldPanelTemplatePortal);

    // Subscribe to the backdrop click
    this._customFieldPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._closeCustomFieldPanel();
      });
  }

  private _closeCustomFieldPanel(): void {
    // If template portal exists and attached...
    if (this._customFieldPanelTemplatePortal && this._customFieldPanelTemplatePortal.isAttached) {
      // Detach it
      this._customFieldPanelTemplatePortal.detach();
    }

    // If overlay exists and attached...
    if (this._customFieldPanelOverlayRef && this._customFieldPanelOverlayRef.hasAttached()) {
      // Detach it
      this._customFieldPanelOverlayRef.detach();
      this._customFieldPanelOverlayRef.dispose();
    }

    this._changeDetectorRef.markForCheck();
  }

  onFieldAdded(fieldDef: FieldDef): void {
    this.addDisplayColumn(fieldDef.name);
    this.fieldDefs = [...this.fieldDefs, fieldDef];
    this.filteredFieldDefs = [...this.filteredFieldDefs, fieldDef];

    this._projectsService
      .getProjects(
        this.getSelectFields(),
        this.queryFilters,
        this._paginator.pageIndex,
        this._paginator.pageSize,
        this.newTable ? this._sortField : this._sort.active,
        this.newTable ? this._sortDirection : this._sort.direction,
        '',
        null,
        '',
        this.queryFilters?.includeChildren ?? false,
        true,
        this.queryFilters?.includeChildren ?? false,
        this.getNestedOptions(),
      )
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this._closeCustomFieldPanel()),
      )
      .subscribe(noop);
  }

  handleFieldChange(event, field) {
    field.label = event.target.value;
    this._accountService
      .updateCustomField(field)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        this.toggleEditDisplayColumnsModel();
        this._changeDetectorRef.markForCheck();
      });
  }

  handleFieldDelete(field) {
    this.confirmService
      .openConfirmationModal({ message: `Are you sure you would like to remove ${field.label} from the layout?` })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        // Mark for check
        this.removeDisplayColumn(field.name);
        this._changeDetectorRef.markForCheck();
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private _saveColumns(): void {
    if (!this.canManagePipeline()) {
      return;
    }
    const tableDefinition = isString(this.pipeline.table_definition)
      ? JSON.parse(this.pipeline.table_definition)
      : this.pipeline.table_definition;

    this.pipeline.table_definition = JSON.stringify({
      ...tableDefinition,
      fields: this.displayColumns,
      columnState: this.newTable ? this.gridApi.getColumnState() : null,
    });

    this._projectsService
      .updatePipeline(this.pipeline)
      .pipe(
        untilComponentDestroyed(this),
        finalize(() => {
          // [SC-26642] Call graphQL if saved filters are active.
          if (this.filterApplied) {
            this.filterProjects(JSON.parse(this.savedFilter?.filters));
          }
        }),
      )
      .subscribe(noop);

    this.getColDefs();
  }

  dropColumns($event: CdkDragDrop<any[]>): void {
    const from: number = $event.previousIndex + 1;
    const to: number = $event.currentIndex + 1;
    if (to !== from) {
      moveItemInArray(this.displayColumns, from, to);
      this._saveColumns();
    }
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(event, this.displayColumns, ['checkbox'], (columns: string[]) => {
      this._saveColumns();
    });
  }

  moveToFirst(columnName: string, columnsList: FieldDef[] = []): FieldDef[] {
    const index = columnsList.findIndex((column) => column?.name === columnName);
    if (index !== -1) {
      const coverImage = columnsList.splice(index, 1);
      columnsList.unshift(coverImage.shift());
      return columnsList;
    }
  }

  searchInput(event) {
    this.isLoading = true;

    this._projectsService
      .getProjects(
        this.displayColumns,
        this.queryFilters,
        0,
        this._pageSize,
        this.getSortByArray(),
        this.sortBy.order,
        event,
        null,
        '',
        this.queryFilters?.includeChildren ?? false,
        true,
        this.queryFilters?.includeChildren ?? false,
        this.getNestedOptions(),
      )
      .pipe(debounceTime(300), untilComponentDestroyed(this))
      .subscribe(() => (this.isLoading = false));
  }

  filterProjects(event: Partial<QueryConfig.QueryFilters>, trackChange = true): void {
    trackChange && this.openTrackStateSnackbar('filters');
    this.isLoading = true;
    this.queryFilters = event;
    this._projectsService
      .getProjects(
        this.getSelectFields(),
        this.queryFilters,
        0,
        this._pageSize,
        this.getSortByArray(),
        this.sortBy.order,
        this.queryFilters.q,
        null,
        '',
        this.queryFilters?.includeChildren ?? false,
        true,
        this.queryFilters?.includeChildren ?? false,
        this.getNestedOptions(),
      )
      .pipe(debounceTime(300), untilComponentDestroyed(this))
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  reloadProjects() {
    this.isLoading = true;
    this._projectsService
      .getProjects(
        this.getSelectFields(),
        this.queryFilters,
        0,
        this._pageSize,
        this.getSortByArray(),
        this.sortBy.order,
        this.queryFilters?.q,
        null,
        '',
        this.queryFilters?.includeChildren ?? false,
        true,
        this.queryFilters?.includeChildren ?? false,
        this.getNestedOptions(),
      )
      .pipe(untilComponentDestroyed(this), take(1))
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  resetQueryFilters() {
    this.queryFilters = {};
    this.saveQueryFilters(this.queryFilters);
    this.savedFilter = null;
    UrlUtils.saveToLocalStorage('savedFilter', '', false);
  }

  duplicatePipeline() {
    this._matDialog.open(PipelineAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { pipelines: this.pipelines, copy_from_pipeline: this.pipeline, creatingView: true },
    });
  }

  navigateToImportsSettings() {
    this._router.navigate(['../../../settings/imports']);
  }

  pageSizeOptions(): number[] {
    return this._pageSizeOptions || [];
  }

  // Reset table scroll position when items per page changes.
  onPageChange(event: PageEvent): void {
    this._pageSize = event.pageSize;
    this.resetScrollPosition = true;
  }

  editField({ id, permissions }: Project, fieldDef: FieldDef): void {
    if (this.isLinkedFieldColumn(fieldDef)) {
      return;
    }

    const { name, meta, locked } = fieldDef;
    if (meta?.readonly || locked || !permissions?.is_editable) return;
    const projectFieldId = this.getProjectFieldId(id, name);
    this.editableDictionary[projectFieldId] = this.editableDictionary[projectFieldId] ?? true;
  }

  onEndEditing({ id }: Project, { name }: FieldDef): void {
    const projectFieldId = this.getProjectFieldId(id, name);
    delete this.editableDictionary[projectFieldId];
  }

  getTooltip(project: Project, fieldDef: FieldDef): string {
    const projectFieldId = this.getProjectFieldId(project.id, fieldDef.name);

    if (this.editableTooltipDictionary[projectFieldId]) {
      return this.editableTooltipDictionary[projectFieldId];
    }

    let showTip = fieldDef.description || fieldDef.meta?.calc_code;

    if (['number', 'decimal'].includes(fieldDef.type)) {
      showTip = showTip || 'use formula e.g. =2+2';
    }

    if (fieldDef.name === 'latest_comment_str') {
      showTip = project['latest_comment_str'];
    }

    if (fieldDef.name === 'description') {
      showTip = project['description'];
    }

    return showTip;
  }

  private getProjectFieldId(projectId: string, fieldName: string): string {
    return `${projectId}-${fieldName}`;
  }

  openCustomDrawer(): void {
    setupAddDealByDrawer(
      {
        projectService: this._projectsService,
        pipelineService: this._pipelineService,
        foldersTabService: this._foldersTabService,
        layoutService: this._layoutService,
        router: this._router,
        matDialog: this._matDialog,
        authService: this._authService,
      },
      {
        pipeline: this.pipeline,
        account: this.account,
        fieldDefs: this.fieldDefs,
        data: { ...this.data, options: { ...this.data.options, pipelines: this.pipelines } },
        projectTemplates: this._projectTemplates,
        createPortfolio: true,
      },
    ).subscribe(noop);
  }

  canChangePipelineVisibleColumns(): boolean {
    return (
      this.isPipelineDynamic() || this._authService.hasPermission(Permission.PROJECT_PIPELINE_CHANGE_VISIBLE_COLUMNS)
    );
  }

  filterGroupByOptions(fieldDefs: FieldDef[]): FieldDef[] {
    return fieldDefs.filter((x) => x.related_to_type === RelatedToType.Project && x.meta?.groupable === true);
  }

  saveGroupBy(reset = false): void {
    if (reset) {
      this.onChangeGroupBy({ value: this.groupByMemento } as MatSelectChange);
      this.manageLocalGroupBySelection('delete');
      return;
    }

    if (this.canManagePipeline()) {
      this.groupByMemento = this.pipeline.table_definition.groupBy;
      this.manageLocalGroupBySelection('delete');
      this.updatePipelineGroupBy(this.pipeline, this.pipeline.table_definition.groupBy)
        .pipe(untilComponentDestroyed(this))
        .subscribe(noop);
    }
  }

  canManagePipeline(): boolean {
    return this._authService.hasPermission(Permission.PIPELINE_MANAGE);
  }

  canAddDeal(): boolean {
    return this._authService.hasPermission(Permission.DEALS_ADD);
  }

  canEditAllSelectedDeals(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.selectedProjects.every((project) => project?.permissions?.is_editable);
    }
    return true;
  }

  canDeleteAllSelectedDeals(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.selectedProjects.every((project) => project?.permissions?.is_deletable);
    } else {
      return this.store.selectSnapshot(UserState.hasPermission(Permission.DEALS_DELETE));
    }
  }

  canSelectColumns(): boolean {
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(Permission.PROJECT_PIPELINE_CHANGE_VISIBLE_COLUMNS));
    }

    return true;
  }

  getLinkedFieldValue(object: any, fieldName: string): { label: string; [key: string]: any }[] {
    return _getLinkedFieldValue(object, fieldName, {
      fieldDefs: this.fieldDefs,
      options: this.options,
    });
  }

  private handleSoftRequiredFields(entity: ProjectStage | ProjectPhase, projectId: string, isStatus = true): boolean {
    if (entity?.required_fields) {
      const project = this.projects.find(({ id }: Project) => id == projectId);
      const dialog = this._matDialog.open(ProjectStatusRequiredFieldsComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { data: this.data, stage: entity, project, isStatus },
      });
      dialog
        .afterClosed()
        .pipe(take(1), untilComponentDestroyed(this))
        .subscribe({
          next: () => {
            this.reloadProjects();
          },
          complete: () => {
            this.onEditEnd();
          },
        });
      return true;
    }
    return false;
  }

  private handleAndGetCurrentPage(pipelineId: number): number {
    if (!this.lastPipelineIdSelected || this.lastPipelineIdSelected !== pipelineId) {
      sessionStorage.setItem('currentPage', '0');
    }
    const currentPage = +sessionStorage.getItem('currentPage') || 0;
    this.lastPipelineIdSelected = pipelineId;
    return currentPage;
  }

  public isPipelineDynamic(): boolean {
    return this.pipeline?.pipeline_type === 'dynamic';
  }

  public hideSaveButton(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return !this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_PUBLIC_CUSTOM_VIEWS));
    }
    return !this.store.selectSnapshot(UserState.hasPermission(Permission.PIPELINE_EDIT_DYNAMIC_FILTER));
  }

  private updatePipelineGroupBy(pipeline: Pipeline, groupBy: string): Observable<Pipeline> {
    if (!this.canManagePipeline()) {
      return of(pipeline);
    }
    return this._pipelineService
      .update(
        {
          ...pipeline,
          table_definition: JSON.stringify({ ...pipeline.table_definition, groupBy }),
        },
        Number(this.account.id),
      )
      .pipe(tap(() => this._projectsService.bustPipelineCache()));
  }

  private manageLocalGroupBySelection(mode: 'change' | 'delete' = 'change', groupBy: string = null): void {
    if (mode === 'change') {
      UrlUtils.saveToLocalStorage(
        this.groupByLocalSelectionKey,
        { ...this.groupByLocalSelection, [this.pipeline.id]: groupBy },
        true,
      );
    } else {
      const stage = { ...this.groupByLocalSelection };
      delete stage[this.pipeline.id];
      UrlUtils.saveToLocalStorage(this.groupByLocalSelectionKey, stage, true);
    }
  }

  private onGetProjectsCatchError = catchError(() => {
    this._snackBar.open('Error while getting the data.', 'error');
    return EMPTY;
  });

  private onGetProjectsCompleted(): void {
    this.isLoading = false;
    this.getColDefs();
    this._changeDetectorRef.detectChanges();
  }

  private setColumnsAttributes() {
    const user = this.store.selectSnapshot(UserState.getUser);
    const isAdmin = user?.roles.some((role) => role.name === ReservedRole.Admin);

    this.columnDefs.map((fieldDef) => {
      if (fieldDef.type === 'select') {
        if (fieldDef.name === 'template_id' && fieldDef.meta?.option_name) {
          const allTemplates = this.data?.options?.[fieldDef.meta.option_name] || [];
          const projectTemplates = allTemplates.filter(
            (template: any) => template.related_to_type === RelatedToType.Project && !template.hidden,
          );
          fieldDef.meta.options = projectTemplates;
        }
      }

      const columnWidth = this.pipeline?.table_definition?.columnState?.find(
        (x) => x.colId === fieldDef.name.split('.')[0],
      )?.width;
      if (fieldDef.meta?.record_type === RelatedToType.User) {
        fieldDef.meta.options = this.store
          //@ts-ignore
          .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
          .map((x) => ({ id: x.id, label: x.name }));
      }
      fieldDef.tsTableOptions = {
        ...fieldDef.tsTableOptions,
        headerName: fieldDef.label,
        field: fieldDef.name,
        filter: true,
        resizable: true,
        lockPinned: true,
        width: columnWidth || 200,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        editable: (params) => {
          if (fieldDef.name === 'record_owner_id' || fieldDef.name === 'team_id') {
            const canEditOwnerOrTeamField = params.data.record_owner_id === user.id.toString() || isAdmin;
            return canEditOwnerOrTeamField;
          }
          return !params.data?.isCategory && !fieldDef.meta?.readonly;
        },
        cellStyle(params) {
          if (params.data?.isCategory) {
            return {
              padding: '0',
              height: '100%',
            };
          } else return nonEditableStyle(fieldDef);
        },
        suppressHeaderMenuButton: false,
        cellEditor: fieldDef.type === 'tags' ? TsTableTagsEditor : null,
      };

      // Add custom renderer for next_task field
      if (fieldDef.name === 'next_task') {
        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          cellRenderer: TableNextTaskRendererComponent,
          editable: false,
        };
      }
    });

    const autoGroupColumnWidth = this.pipeline?.table_definition?.columnState?.find(
      (x) => x.colId === 'ag-Grid-AutoColumn',
    )?.width;
    let groupField = this.displayColumns[0];

    const fieldDef = this.lookupField(groupField);
    const formatValue = fieldDef ? this.tsTableGroupedService.setValueFormatter(fieldDef) : (value: any) => value;
    this.autoGroupColumnDef = {
      headerName: fieldDef?.label || 'Name',
      field: groupField,
      suppressMovable: true,
      rowDrag: false,
      suppressHeaderMenuButton: true,
      editable: false,
      width: autoGroupColumnWidth,
      suppressAutoSize: true,
      suppressSizeToFit: true,
      cellRenderer: DealsGroupTableRenderer,
      cellRendererParams: {
        suppressCount: true,
        formatValue,
      },
      context: {
        expandedProjects: this.expandedProjects,
        isLoadingChildren: this.isLoadingChildren,
        fetchChildren: (project, index) => {
          if (!project.loaded) {
            this.expand(project, index);
          }
        },
        fieldDef,
      },
    };
  }

  private getColDefs(): void {
    if (this.newTable) {
      this.columnDefs = this.displayColumns.slice(1).map((column) => this.lookupField(column));
      this.setColumnsAttributes();
    }
  }

  loadRowData() {
    const projectsMap = new Map<string, any>();
    const rootProjects = [];

    this.projects.forEach((project) => {
      if (project.id) {
        projectsMap.set(project.id.toString(), {
          ...project,
          children: project.children || [],
        });
      }
    });

    this.projects.forEach((project) => {
      if (project.is_child === 'true' && project.parent_id) {
        const parentProject = projectsMap.get(project.parent_id.toString());
        if (parentProject) {
          parentProject.children.push(project);
        }
      }
    });

    this.projects.forEach((project) => {
      const projectInMap = projectsMap.get(project.id?.toString());
      if (projectInMap && !projectInMap.parent_id) {
        rootProjects.push(projectInMap);
      }
    });
    //@ts-ignore
    const groups = this.groupedProjects.filter((group) => group.summaryColumns);
    const groupByField = this.pipeline?.table_definition?.groupBy;
    let formattedGroupByField = groupByField ? this.groupFieldValue(groupByField) : '';
    if (groupByField?.includes('_id')) {
      formattedGroupByField = `${this.groupFieldValue(groupByField)}.name`;
    }

    this.rowData = this.tsTableGroupedService.flattenDataWithHierarchyAndGrouping(
      rootProjects,
      groups,
      (project) => (project.id ? project.id.toString() : ''),
      (project) => (project.parent_id ? project.parent_id.toString() : null),
      formattedGroupByField,
    );
  }

  onSelectionChanged(event: Partial<Project>[]): void {
    const prevSet = new Set(this.previousSelectedIds);
    const currSet = new Set(event.map((i) => Number(i.id)));

    const added = [...currSet].filter((id) => !prevSet.has(id));
    const removed = [...prevSet].filter((id) => !currSet.has(id));

    const resultSet = new Set(prevSet);

    added.forEach((id) => {
      const item = event.find((i) => Number(i.id) === id)!;
      if (item.is_parent === 'true') {
        resultSet.add(id);
        item.children?.forEach((child) => resultSet.add(Number(child.id)));
      } else {
        resultSet.add(id);
      }
    });

    removed.forEach((id) => {
      resultSet.delete(id);
      const parent = this.findParentNodeForChild(id);
      if (parent) {
        resultSet.delete(Number(parent.id));
      }
    });

    const finalIds = Array.from(resultSet);
    if (this.gridApi) {
      this.gridApi.forEachNode((node) => {
        const id = Number(node.data.id);
        node.setSelected(finalIds.includes(id));
      });
    }
    this.selection.setSelection(...finalIds);
    this.previousSelectedIds = finalIds;
  }

  private findParentNodeForChild(childId: number): Project | null {
    let found: Project | null = null;
    this.gridApi?.forEachNode((node) => {
      const data = node.data as Project;
      if (data.is_parent === 'true' && data.children?.some((c) => Number(c.id) === childId)) {
        found = data;
      }
    });
    return found;
  }

  onProjectCellEditingStopped(event: CellEditingStoppedEvent<Partial<Project>>): void {
    if (this.handleProjectStatusChange(event)) {
      return;
    }
    const newValue: Partial<Project> = {
      [event.colDef.field]: event.data[event.colDef.field],
      id: event.data.id,
    };

    this.onUpdate(newValue, event.data as Project);
  }

  onSortChanged(event: SortChangedEvent): void {
    this.openTrackStateSnackbar('sort');
    if (event.columns && event.columns.length > 0) {
      const sortedColumn = event.columns.filter((col) => col.getSort());
      const sortField = sortedColumn[0]?.getColDef().field || 'project_stage_rank';
      const sortDirection = sortedColumn[0]?.getSort() || 'asc';

      this._sortField = sortField;
      this._sortDirection = sortDirection as 'asc' | 'desc';

      this.sortBy = { field: sortField, order: sortDirection };
      this._paginator.pageIndex = 0;
    } else {
      this._sortField = null;
      this._sortDirection = null;
      this._paginator.pageIndex = 0;
    }

    this.sortChangedSubject.next();
  }

  checkTable() {
    const account = this._accountService.getCurrentAccount();
    this.newTable = account.features.includes(FeatureFlag.NEW_TABLE);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  getDataPath(data: any) {
    return data.path;
  }

  onColumnResized(event) {
    this.columnResizeSubject.next();
  }

  onDataUpdate() {
    if (this.gridApi) {
      const columnState = this.gridApi.getColumnState();
      this.gridApi.applyColumnState({ state: columnState });
    }
  }

  private captureOriginalStates(): void {
    this._originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      sort: this.getSortState(),
      queryFilters: this.getQueryFiltersState(),
      groupBy: this.getGroupByState(),
    };
  }

  private getDisplayColumnsState(): string[] {
    return [...this.displayColumns];
  }

  private openTrackStateSnackbar(changeType: ChangeType): void {
    if (this._changeTriggers.size === 0) {
      this.captureOriginalStates();
    }
    this._changeTriggers.add(changeType);
    this._snackBar.openApplyChanges(
      this.resetToOriginalStates.bind(this),
      this.canManagePipeline() ? this.saveAllChanges.bind(this) : null,
    );
  }

  private setDisplayColumnsState(value: string[]) {
    this._originalStates.displayColumns = value;
  }

  private setSortState(value: TableSortState) {
    this._originalStates.sort = value;
  }

  private setQueryFiltersState() {
    this._originalStates.queryFilters = this.queryFilters;
    this.saveQueryFilters(this.queryFilters);
  }

  private setGroupByState() {
    this._originalStates.groupBy = this.pipeline.table_definition.groupBy;
  }

  private getSortState(): TableSortState {
    return { active: this._sortField, direction: this._sortDirection };
  }

  private getQueryFiltersState(): any {
    return this.queryFilters;
  }

  private getGroupByState(): any {
    return this.pipeline.table_definition.groupBy;
  }

  private saveAllChanges(): void {
    if (this._changeTriggers.has('columns')) {
      this.setDisplayColumnsState(this.displayColumns);
      this._saveColumns();
    }

    if (this._changeTriggers.has('sort')) {
      UrlUtils.saveToLocalStorage('sortBy', JSON.stringify(this.sortBy));
      this.setSortState(this.getSortState());
    }

    if (this._changeTriggers.has('filters')) {
      this.setQueryFiltersState();
    }

    if (this._changeTriggers.has('groupBy')) {
      this.manageLocalGroupBySelection('change', this.pipeline.table_definition.groupBy);
      this.setGroupByState();
    }

    this.clearChangeTriggers();
  }

  private resetToOriginalStates(): void {
    if (this._changeTriggers.has('columns')) {
      this.displayColumns = [...(this?._originalStates?.displayColumns ?? [])];
      this.cleanseDisplayColumns();
      this._saveColumns();
    }

    if (this._changeTriggers.has('sort')) {
      const sortState = this._originalStates.sort;
      this._sortField = sortState.active;
      this._sortDirection = sortState.direction as 'asc' | 'desc';
      this.sortBy = {
        field: sortState.active,
        order: sortState.direction,
      };
      this.getProjectsBySort()
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => (this.isLoading = false));
      this.gridApi.applyColumnState({
        state: [
          {
            colId: sortState.active,
            sort: sortState.direction,
          },
        ],
      });
    }

    if (this._changeTriggers.has('filters')) {
      this.queryFilters = this._originalStates.queryFilters;
      this.saveQueryFilters(this.queryFilters);
      this.filterProjects(this.queryFilters, false);
    }

    if (this._changeTriggers.has('groupBy')) {
      this.pipeline.table_definition.groupBy = this._originalStates.groupBy;
      this.onChangeGroupBy({ value: this._originalStates.groupBy } as MatSelectChange);
    }

    this.clearChangeTriggers();
    this._changeDetectorRef.markForCheck();
  }

  private clearChangeTriggers(): void {
    this._changeTriggers.clear();
  }

  private handleProjectStatusChange(event: CellEditingStoppedEvent<Partial<Project>>): boolean {
    if (['project_stage_id'].includes(event.colDef.field)) {
      const fullProject = this.projects.find(({ id }: Project) => id == event.data.id);
      this.updateProjectStatus
        .update(this.data, fullProject, { id: +event.data.id, [event.colDef.field]: event.data[event.colDef.field] })
        .pipe(untilComponentDestroyed(this))
        .subscribe({
          next: (result) => {
            this.reloadProjects();

            if (!result && !this.newTable) {
              this._sort.sort({ id: 'status', start: 'asc', disableClear: true });
            }

            this.isLoading = false;
          },
          error: () => (this.isLoading = false),
        });

      return true;
    }

    return false;
  }

  private stagesFilterFn(options: any[], model: Partial<Project>): any[] {
    if (!options?.length) {
      return [];
    }

    return options.filter((x) => x.pipeline_ids.includes(+model.pipeline_id));
  }

  openPortfolioActionsDialog(mode: 'new' | 'existing' | 'split') {
    let deals: any[] = this.projects
      .filter((x) => this.selection.selected.includes(+x.id))
      .map(({ id, is_parent, parent_id, title }) => ({ id, parent_id, is_parent: is_parent === 'true', title }));

    this.dialog
      .open(PortfolioActionComponent, {
        data: {
          mode,
          destination_portfolio_pipeline_id: this.pipeline.id,
          deals,
          templates: this._projectTemplates,
          pipelines: this.pipelines,
        },
        ...TsDialogComponent.getDefaultDialogConfig(),
      })
      .closed.subscribe((res) => {
        this.reloadProjects();
      });
  }
}
